<template>
  <div class="teacher_data">
    <div class="personal_data_wrapper fadeIn">
      <!-- Start Hero Driver Header Card -->
      <div class="driver-hero-card mb-5">
        <div class="hero-main-info">
          <div class="user-avatar-wrapper">
            <img
              v-if="userInfo.avatar"
              :src="userInfo.avatar"
              alt="Avatar"
              class="user-avatar"
            />
            <div v-else class="avatar-placeholder">
              <i class="fas fa-steering-wheel"></i>
            </div>
            <span
              class="status-indicator-dot"
              :class="userInfo.is_online ? 'online' : 'offline'"
            ></span>
          </div>

          <div class="user-title-details">
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <h2 class="user-name">{{ userInfo.full_name || "---" }}</h2>
              <span class="user-id-badge">ID: #{{ userInfo.id }}</span>
            </div>
            <p class="user-subtitle dir-ltr" v-if="userInfo.phone">
              <i class="fas fa-phone-alt ml-1"></i>
              {{ userInfo.phone_code }} {{ userInfo.phone }}
            </p>
          </div>
        </div>

        <div class="hero-status-pills">
          <span
            class="status-pill"
            :class="userInfo.is_active ? 'pill-success' : 'pill-danger'"
          >
            <i
              :class="
                userInfo.is_active
                  ? 'fas fa-check-circle'
                  : 'fas fa-times-circle'
              "
            ></i>
            {{ userInfo.is_active ? "مفعل" : "غير مفعل" }}
          </span>

          <span
            class="status-pill"
            :class="userInfo.is_ban ? 'pill-danger' : 'pill-info'"
          >
            <i
              :class="userInfo.is_ban ? 'fas fa-ban' : 'fas fa-shield-alt'"
            ></i>
            {{ userInfo.is_ban ? "محظور" : "غير محظور" }}
          </span>

          <span
            class="status-pill"
            :class="userInfo.is_online ? 'pill-teal' : 'pill-warning'"
          >
            <i
              :class="userInfo.is_online ? 'fas fa-wifi' : 'fas fa-wifi-slash'"
            ></i>
            {{ userInfo.is_online ? "متصل" : "غير متصل" }}
          </span>
        </div>
      </div>
      <!-- End Hero Driver Header Card -->

      <!-- Start Stats Grid -->
      <div class="order-details-page mb-5">
        <div class="magic-stats-grid">
          <!-- Full Name -->
          <div class="magic-stat-card">
            <div class="stat-icon-wrapper blue-gradient">
              <i class="fas fa-user"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.name") }}</span>
              <h4 class="stat-value">{{ userInfo.full_name || "---" }}</h4>
            </div>
          </div>

          <!-- Phone Number -->
          <div class="magic-stat-card" v-if="userInfo.phone">
            <div class="stat-icon-wrapper green-gradient">
              <i class="fas fa-phone-alt"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.phone") }}</span>
              <h4 class="stat-value" dir="ltr">
                {{ userInfo.phone_code }} {{ userInfo.phone }}
              </h4>
            </div>
          </div>

          <!-- Email -->
          <div class="magic-stat-card" v-if="userInfo.email">
            <div class="stat-icon-wrapper orange-gradient">
              <i class="fas fa-envelope"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.email") }}</span>
              <h4 class="stat-value">{{ userInfo.email }}</h4>
            </div>
          </div>

          <!-- ID Number -->
          <div class="magic-stat-card" v-if="userInfo.id_number">
            <div class="stat-icon-wrapper purple-gradient">
              <i class="fas fa-id-card"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">رقم الهوية</span>
              <h4 class="stat-value">{{ userInfo.id_number }}</h4>
            </div>
          </div>

          <!-- Last Login Date -->
          <div class="magic-stat-card" v-if="userInfo.last_login_at">
            <div class="stat-icon-wrapper indigo-gradient">
              <i class="fas fa-clock"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">آخر تسجيل دخول</span>
              <h4 class="stat-value">{{ userInfo.last_login_at }}</h4>
            </div>
          </div>

          <!-- Login Count -->
          <div
            class="magic-stat-card"
            v-if="userInfo.login_count !== undefined"
          >
            <div class="stat-icon-wrapper cyan-gradient">
              <i class="fas fa-history"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">عدد مرات تسجيل الدخول</span>
              <h4 class="stat-value">{{ userInfo.login_count || 0 }}</h4>
            </div>
          </div>

          <!-- Country -->
          <div class="magic-stat-card" v-if="userInfo.country">
            <div class="stat-icon-wrapper teal-gradient">
              <i class="fas fa-globe-africa"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.country") }}</span>
              <h4 class="stat-value">{{ userInfo.country.name }}</h4>
            </div>
          </div>

          <!-- City -->
          <div class="magic-stat-card" v-if="userInfo.city">
            <div class="stat-icon-wrapper pink-gradient">
              <i class="fas fa-city"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.city") }}</span>
              <h4 class="stat-value">{{ userInfo.city.name }}</h4>
            </div>
          </div>

          <!-- Date of Birth -->
          <div class="magic-stat-card" v-if="userInfo.date_of_birth">
            <div class="stat-icon-wrapper yellow-gradient">
              <i class="fas fa-birthday-cake"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">تاريخ الميلاد</span>
              <h4 class="stat-value">{{ userInfo.date_of_birth }}</h4>
            </div>
          </div>

          <!-- Activation Status -->
          <div class="magic-stat-card" v-if="userInfo.is_active !== undefined">
            <div
              class="stat-icon-wrapper"
              :class="userInfo.is_active ? 'green-gradient' : 'red-gradient'"
            >
              <i
                :class="
                  userInfo.is_active ? 'fas fa-user-check' : 'fas fa-user-times'
                "
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">مفعل ؟</span>
              <h4
                class="stat-value"
                :class="userInfo.is_active ? 'text-success' : 'text-danger'"
              >
                {{ userInfo.is_active ? "مفعل" : "غير مفعل" }}
              </h4>
            </div>
          </div>

          <!-- Ban Status -->
          <div class="magic-stat-card" v-if="userInfo.is_ban !== undefined">
            <div
              class="stat-icon-wrapper"
              :class="userInfo.is_ban ? 'red-gradient' : 'green-gradient'"
            >
              <i
                :class="userInfo.is_ban ? 'fas fa-ban' : 'fas fa-shield-alt'"
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">محظور ؟</span>
              <h4
                class="stat-value"
                :class="userInfo.is_ban ? 'text-danger' : 'text-success'"
              >
                {{ userInfo.is_ban ? "محظور" : "غير محظور" }}
              </h4>
            </div>
          </div>

          <!-- Online Status -->
          <div class="magic-stat-card" v-if="userInfo.is_online !== undefined">
            <div
              class="stat-icon-wrapper"
              :class="userInfo.is_online ? 'teal-gradient' : 'yellow-gradient'"
            >
              <i
                :class="
                  userInfo.is_online ? 'fas fa-wifi' : 'fas fa-wifi-slash'
                "
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">متصل ؟</span>
              <h4
                class="stat-value"
                :class="userInfo.is_online ? 'text-success' : 'text-danger'"
              >
                {{ userInfo.is_online ? "متصل" : "غير متصل" }}
              </h4>
            </div>
          </div>

          <!-- Admin Active User Status -->
          <div
            class="magic-stat-card"
            v-if="userInfo.is_admin_active_user !== undefined"
          >
            <div
              class="stat-icon-wrapper"
              :class="
                userInfo.is_admin_active_user
                  ? 'indigo-gradient'
                  : 'red-gradient'
              "
            >
              <i
                :class="
                  userInfo.is_admin_active_user
                    ? 'fas fa-user-shield'
                    : 'fas fa-user-lock'
                "
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">مفعل من الإدارة ؟</span>
              <h4
                class="stat-value"
                :class="
                  userInfo.is_admin_active_user ? 'text-success' : 'text-danger'
                "
              >
                {{ userInfo.is_admin_active_user ? "مفعل" : "غير مفعل" }}
              </h4>
            </div>
          </div>
        </div>
      </div>
      <!-- End Stats Grid -->

      <!-- Start Working Hours Section (Dedicated Creative Card) -->
      <div
        class="custom-section-card mb-4"
        v-if="workingTimesList && workingTimesList.length"
      >
        <div
          class="section-card-header d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4 pb-3 border-bottom"
        >
          <div class="d-flex align-items-center gap-3">
            <div
              class="section-icon-wrapper"
              style="
                background: linear-gradient(135deg, #783d73 0%, #a05a99 100%);
              "
            >
              <i class="fas fa-calendar-alt text-white"></i>
            </div>
            <div>
              <h4
                class="section-title mb-1"
                style="font-weight: 800; font-size: 1.15rem; color: #1e293b"
              >
                أوقات ومواعيد العمل المجدولة
              </h4>
              <p
                class="section-subtitle mb-0 text-muted"
                style="font-size: 13px"
              >
                الجدول الأسبوعي المعتمد لساعات وفترات عمل المندوب
              </p>
            </div>
          </div>
          <span
            class="badge px-3 py-2"
            style="
              background: rgba(120, 61, 115, 0.1);
              color: #783d73;
              font-weight: 700;
              font-size: 13px;
              border-radius: 20px;
              border: 1px solid rgba(120, 61, 115, 0.2);
            "
          >
            <i class="fas fa-check-circle ml-1"></i>
            {{ activeWorkingDaysCount }} أيام عمل مفعلة
          </span>
        </div>

        <div class="weekly-schedule-grid">
          <div
            v-for="(wt, wIdx) in workingTimesList"
            :key="wIdx"
            class="day-schedule-card"
            :class="wt.enabled ? 'day-active' : 'day-inactive'"
          >
            <div
              class="day-card-top d-flex align-items-center justify-content-between mb-2"
            >
              <span class="day-name font-weight-bold" style="font-size: 14.5px">
                {{ getDayArabicName(wt) }}
              </span>
              <span
                class="day-status-pill"
                :class="wt.enabled ? 'active-pill' : 'inactive-pill'"
              >
                <i
                  :class="
                    wt.enabled ? 'fas fa-dot-circle' : 'fas fa-times-circle'
                  "
                ></i>
                {{ wt.enabled ? "مفعل" : "إجازة" }}
              </span>
            </div>

            <div class="day-times-body" v-if="wt.enabled">
              <div
                v-if="wt.times && wt.times.length"
                class="d-flex flex-column gap-2"
              >
                <div
                  v-for="(t, tIdx) in wt.times"
                  :key="tIdx"
                  class="time-slot-chip d-flex align-items-center justify-content-between px-2 py-1 rounded"
                >
                  <span class="text-muted" style="font-size: 11.5px">
                    <i class="far fa-clock ml-1" style="color: #783d73"></i>
                    فترة {{ tIdx + 1 }}:
                  </span>
                  <span
                    class="time-range font-weight-bold"
                    dir="ltr"
                    style="font-size: 13px; color: #1e293b"
                  >
                    {{ (t.start_time || t.from || "").slice(0, 5) }} -
                    {{ (t.end_time || t.to || "").slice(0, 5) }}
                  </span>
                </div>
              </div>
              <div
                v-else-if="wt.from"
                class="time-slot-chip d-flex align-items-center justify-content-between px-2 py-1 rounded"
              >
                <span class="text-muted" style="font-size: 11.5px">
                  <i class="far fa-clock ml-1" style="color: #783d73"></i>
                  الفترة:
                </span>
                <span
                  class="time-range font-weight-bold"
                  dir="ltr"
                  style="font-size: 13px; color: #1e293b"
                >
                  {{ wt.from.slice(0, 5) }} - {{ wt.to.slice(0, 5) }}
                </span>
              </div>
              <div
                v-else
                class="text-muted text-center py-2"
                style="font-size: 12px"
              >
                ساعات غير محددة
              </div>
            </div>

            <div
              class="day-times-body text-center py-3 text-muted"
              v-else
              style="font-size: 12.5px"
            >
              <i class="fas fa-bed mb-1 d-block opacity-50"></i>
              يوم عطلة
            </div>
          </div>
        </div>
      </div>
      <!-- End Working Hours Section -->

      <!-- Start Covered Districts Section (Dedicated Creative Card) -->
      <div
        class="custom-section-card mb-5"
        v-if="userInfo.districts && userInfo.districts.length"
      >
        <div
          class="section-card-header d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 pb-3 border-bottom"
        >
          <div class="d-flex align-items-center gap-3">
            <div
              class="section-icon-wrapper"
              style="
                background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
              "
            >
              <i class="fas fa-draw-polygon text-white"></i>
            </div>
            <div>
              <h4
                class="section-title mb-1"
                style="font-weight: 800; font-size: 1.15rem; color: #1e293b"
              >
                نطاق العمل والأحياء المغطاة
              </h4>
              <p
                class="section-subtitle mb-0 text-muted"
                style="font-size: 13px"
              >
                الأحياء والمناطق المخصصة لعمليات التوصيل للمندوب
              </p>
            </div>
          </div>
          <span
            class="badge px-3 py-2"
            style="
              background: rgba(139, 92, 246, 0.1);
              color: #8b5cf6;
              font-weight: 700;
              font-size: 13px;
              border-radius: 20px;
              border: 1px solid rgba(139, 92, 246, 0.2);
            "
          >
            {{ userInfo.districts.length }} أحياء مشمولة
          </span>
        </div>

        <div class="d-flex flex-wrap gap-2 pt-2">
          <span
            v-for="dist in userInfo.districts"
            :key="dist.id"
            class="district-tag d-flex align-items-center gap-2 px-3 py-2 rounded-lg"
          >
            <i class="fas fa-map-marker-alt text-danger"></i>
            <span
              class="font-weight-bold"
              style="font-size: 13px; color: #1e293b"
            >
              {{ dist.name || (dist.ar ? dist.ar.name : dist.name) }}
            </span>
          </span>
        </div>
      </div>
      <!-- End Covered Districts Section -->
    </div>
  </div>
</template>

<script>
export default {
  props: ["userInfo"],

  data() {
    return {
      center: {},
      markers: null,
      coordinates_to_edit: { lat: 0, lng: 0 },
    };
  },

  computed: {
    isActive() {
      return this.userInfo.is_active ? this.$t("active") : this.$t("inactive");
    },
    isBan() {
      return this.userInfo.is_ban ? this.$t("ban") : this.$t("inban");
    },
    isActiveManager() {
      return this.userInfo.is_ban ? "غير مفعل" : " مفعل";
    },
    workingTimesList() {
      const wt =
        this.userInfo.working_times ||
        (this.userInfo.driver && this.userInfo.driver.working_times) ||
        [];
      return Array.isArray(wt)
        ? wt
        : wt && typeof wt === "object"
        ? Object.values(wt)
        : [];
    },
    activeWorkingDaysCount() {
      return this.workingTimesList.filter((w) => w && w.enabled).length;
    },
  },

  methods: {
    getDayArabicName(wt) {
      if (!wt) return "";
      const map = {
        saturday: "السبت",
        sunday: "الأحد",
        monday: "الإثنين",
        tuesday: "الثلاثاء",
        wednesday: "الأربعاء",
        thursday: "الخميس",
        friday: "الجمعة",
      };
      const rawDay = (wt.day || wt.day_name || "").trim().toLowerCase();
      return map[rawDay] || wt.day_name || wt.day || (wt.ar ? wt.ar.day : "");
    },
  },
};
</script>

<style lang="scss" scoped>
/* Driver Hero Banner Card */
.driver-hero-card {
  background: var(--cardBgColor, #ffffff);
  border-radius: 24px;
  padding: 24px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--borderColor, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;

  .hero-main-info {
    display: flex;
    align-items: center;
    gap: 20px;

    .user-avatar-wrapper {
      position: relative;
      width: 72px;
      height: 72px;

      .user-avatar {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        object-fit: cover;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        border: 3px solid #ffffff;
      }

      .avatar-placeholder {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        background: linear-gradient(
          135deg,
          rgba(120, 61, 115, 0.15) 0%,
          rgba(200, 70, 227, 0.08) 100%
        );
        color: var(--mainColor-2, #783d73);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.8rem;
        border: 2px solid rgba(120, 61, 115, 0.2);
      }

      .status-indicator-dot {
        position: absolute;
        bottom: -2px;
        inset-inline-end: -2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 3px solid #ffffff;

        &.online {
          background-color: #10b981;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
        }

        &.offline {
          background-color: #94a3b8;
        }
      }
    }

    .user-title-details {
      .user-name {
        font-size: 1.4rem;
        font-weight: 800;
        color: var(--mainColor-2, #783d73);
        margin: 0;
      }

      .user-id-badge {
        background: rgba(120, 61, 115, 0.1);
        color: var(--mainColor-2, #783d73);
        font-size: 12px;
        font-weight: 700;
        padding: 3px 10px;
        border-radius: 20px;
        border: 1px solid rgba(120, 61, 115, 0.15);
      }

      .user-subtitle {
        font-size: 13.5px;
        color: #64748b;
        margin: 0;
        font-weight: 600;
      }
    }
  }

  .hero-status-pills {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    .status-pill {
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      &.pill-success {
        background: rgba(16, 185, 129, 0.12);
        color: #10b981;
        border: 1px solid rgba(16, 185, 129, 0.2);
      }

      &.pill-danger {
        background: rgba(239, 68, 68, 0.12);
        color: #ef4444;
        border: 1px solid rgba(239, 68, 68, 0.2);
      }

      &.pill-info {
        background: rgba(59, 130, 246, 0.12);
        color: #3b82f6;
        border: 1px solid rgba(59, 130, 246, 0.2);
      }

      &.pill-teal {
        background: rgba(20, 184, 166, 0.12);
        color: #14b8a6;
        border: 1px solid rgba(20, 184, 166, 0.2);
      }

      &.pill-warning {
        background: rgba(245, 158, 11, 0.12);
        color: #f59e0b;
        border: 1px solid rgba(245, 158, 11, 0.2);
      }
    }
  }
}

/* Magic Stats Grid */
.magic-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.magic-stat-card {
  background: var(--cardBgColor, #ffffff);
  border-radius: 20px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid var(--borderColor, #e2e8f0);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 35px rgba(120, 61, 115, 0.12);
    border-color: rgba(120, 61, 115, 0.25);

    .stat-icon-wrapper {
      transform: scale(1.1) rotate(4deg);
    }
  }

  .stat-icon-wrapper {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.4rem;
    color: #ffffff;
    transition: transform 0.35s ease;
    flex-shrink: 0;

    &.blue-gradient {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      box-shadow: 0 8px 20px rgba(59, 130, 246, 0.28);
    }
    &.purple-gradient {
      background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
      box-shadow: 0 8px 20px rgba(139, 92, 246, 0.28);
    }
    &.orange-gradient {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      box-shadow: 0 8px 20px rgba(245, 158, 11, 0.28);
    }
    &.green-gradient {
      background: linear-gradient(135deg, #10b981 0%, #047857 100%);
      box-shadow: 0 8px 20px rgba(16, 185, 129, 0.28);
    }
    &.cyan-gradient {
      background: linear-gradient(135deg, #06b6d4 0%, #0e7490 100%);
      box-shadow: 0 8px 20px rgba(6, 182, 212, 0.28);
    }
    &.pink-gradient {
      background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
      box-shadow: 0 8px 20px rgba(236, 72, 153, 0.28);
    }
    &.red-gradient {
      background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
      box-shadow: 0 8px 20px rgba(239, 68, 68, 0.28);
    }
    &.indigo-gradient {
      background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
      box-shadow: 0 8px 20px rgba(99, 102, 241, 0.28);
    }
    &.teal-gradient {
      background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
      box-shadow: 0 8px 20px rgba(20, 184, 166, 0.28);
    }
    &.yellow-gradient {
      background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%);
      box-shadow: 0 8px 20px rgba(234, 179, 8, 0.28);
    }
  }

  .stat-content {
    display: flex;
    flex-direction: column;

    .stat-label {
      font-size: 0.85rem;
      color: #64748b;
      margin-bottom: 4px;
      font-weight: 600;
    }

    .stat-value {
      font-size: 1.18rem;
      font-weight: 800;
      color: var(--mainText, #2c2c2c);
      margin: 0;
    }
  }
}

/* Custom Section Card (Dedicated Row Cards) */
.custom-section-card {
  background: var(--cardBgColor, #ffffff);
  border-radius: 24px;
  padding: 24px 28px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--borderColor, #e2e8f0);
  transition: all 0.3s ease;

  .section-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.3rem;
    box-shadow: 0 6px 16px rgba(120, 61, 115, 0.25);
  }
}

.weekly-schedule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 16px;
}

.day-schedule-card {
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.25s ease;

  &.day-active {
    background: #ffffff;
    border-color: rgba(120, 61, 115, 0.25);
    box-shadow: 0 4px 12px rgba(120, 61, 115, 0.06);

    .day-name {
      color: #783d73;
    }
  }

  &.day-inactive {
    background: rgba(241, 245, 249, 0.6);
    opacity: 0.75;

    .day-name {
      color: #64748b;
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }

  .day-status-pill {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &.active-pill {
      background: rgba(16, 185, 129, 0.12);
      color: #10b981;
    }

    &.inactive-pill {
      background: rgba(148, 163, 184, 0.15);
      color: #64748b;
    }
  }

  .time-slot-chip {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
  }
}

.district-tag {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: #8b5cf6;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(139, 92, 246, 0.1);
  }
}

// Dark Mode Overrides
html.darkTheme {
  .driver-hero-card {
    background: #1e1e2d;
    border-color: #2b2b40;
  }

  .magic-stat-card {
    background: #1e1e2d;
    border-color: #2b2b40;

    .stat-content .stat-value {
      color: #f1f5f9;
    }
  }

  .custom-section-card {
    background: #1e1e2d;
    border-color: #2b2b40;

    .section-title {
      color: #f1f5f9 !important;
    }
  }

  .day-schedule-card {
    background: #151521;
    border-color: #2b2b40;

    &.day-active {
      background: #1a1a27;
      border-color: rgba(120, 61, 115, 0.4);

      .time-slot-chip {
        background: #151521;
        border-color: #2b2b40;

        .time-range {
          color: #f1f5f9 !important;
        }
      }
    }
  }

  .district-tag {
    background: #1a1a27;
    border-color: #2b2b40;

    span {
      color: #f1f5f9 !important;
    }
  }
}
</style>
